import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent, type ReactNode } from "react";
import { cleanFlatNo, cleanPhone, contactProblem, type ContactProblem } from "../lib/contact.js";
import { fetchOwnContact, saveContactDetails } from "../lib/members.js";
import type { ContactDetails } from "../lib/types.js";
import { Button, Field, inputStyle, Notice } from "./ui.js";

/** What is in the two inputs, as typed. Blank is "not held". */
export interface ContactDraft {
  flatNo: string;
  phone: string;
}

export const draftOf = (details: ContactDetails | undefined): ContactDraft => ({
  flatNo: details?.flatNo ?? "",
  phone: details?.phone ?? "",
});

/** Both on file — what a tournament that asks for contact details needs. */
export const holdsBoth = (details: ContactDetails | undefined): boolean =>
  Boolean(details?.flatNo && details.phone);

const sentence = (problem: ContactProblem) =>
  problem.message.charAt(0).toUpperCase() + problem.message.slice(1) + ".";

/**
 * A flat number and a phone number, both optional.
 *
 * The one place these are typed, so the join form, the profile and the register screen cannot
 * disagree about what is accepted. The rules are lib/contact.ts's, which mirror the database's;
 * a field's problem shows once somebody has left it, rather than telling them their phone
 * number is too short while they are still typing it. Whoever renders this decides what to do
 * about a problem — every caller refuses to save while `contactProblem` returns one.
 */
export function ContactFields({
  value,
  onChange,
  needBoth = false,
}: {
  value: ContactDraft;
  onChange: (next: ContactDraft) => void;
  /** Asked for by a tournament rather than offered, so neither is labelled optional. */
  needBoth?: boolean;
}) {
  const [left, setLeft] = useState({ flatNo: false, phone: false });
  const problem = contactProblem(value.flatNo, value.phone);
  const shown = (field: ContactProblem["field"]) =>
    problem?.field === field && left[field] ? problem : null;

  const flatProblem = shown("flatNo");
  const phoneProblem = shown("phone");
  const optional = needBoth ? "" : "Optional. ";

  return (
    <>
      <Field label="Flat number" hint={flatProblem ? undefined : `${optional}For example B-1203.`}>
        <input
          value={value.flatNo}
          onChange={(event) => onChange({ ...value, flatNo: event.target.value })}
          onBlur={() => setLeft((was) => ({ ...was, flatNo: true }))}
          aria-invalid={flatProblem ? true : undefined}
          autoComplete="address-line2"
          style={inputStyle}
        />
        {flatProblem ? <Problem problem={flatProblem} /> : null}
      </Field>

      <Field
        label="Phone number"
        hint={phoneProblem ? undefined : `${optional}With the country code if it isn't Indian.`}
      >
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={value.phone}
          onChange={(event) => onChange({ ...value, phone: event.target.value })}
          onBlur={() => setLeft((was) => ({ ...was, phone: true }))}
          aria-invalid={phoneProblem ? true : undefined}
          style={inputStyle}
        />
        {phoneProblem ? <Problem problem={phoneProblem} /> : null}
      </Field>
    </>
  );
}

function Problem({ problem }: { problem: ContactProblem }) {
  return (
    <span className="mt-1.5 block text-sm" style={{ color: "var(--color-attention)" }}>
      {sentence(problem)}
    </span>
  );
}

/**
 * Somebody's details as a line: "Flat B-1203 · 9845012345", the number a link that dials it.
 * `whose` names a guardian standing in for a child. Nothing at all when there is nothing held
 * and nobody to name.
 */
export function ContactLine({
  flatNo,
  phone,
  whose = null,
}: ContactDetails & { whose?: string | null }) {
  const parts: ReactNode[] = [];
  if (whose) parts.push(whose);
  if (flatNo) parts.push(`Flat ${flatNo}`);
  if (phone) {
    parts.push(
      <a key="phone" href={`tel:${phone}`} className="underline">
        {phone}
      </a>,
    );
  }
  if (parts.length === 0) return null;

  return (
    <p className="mt-1 text-sm text-ink-muted">
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 ? " · " : null}
          {part}
        </Fragment>
      ))}
    </p>
  );
}

/** Your own details. One query key, so the profile and the register screen share one fetch. */
export function useOwnContact(memberId: string | null) {
  return useQuery({
    queryKey: ["own-contact", memberId],
    queryFn: () => fetchOwnContact(memberId!),
    enabled: Boolean(memberId),
  });
}

/**
 * Your details, with a Save — the profile's section and the register screen's prompt.
 *
 * Save is offered only when something changed and nothing is wrong — and, where a tournament
 * needs both, only once both are there, since saving half would leave it still asking. What
 * was saved comes back through the shared query rather than from the form, so the register
 * screen sees the new details the moment they land and stops asking.
 */
export function ContactEditor({
  memberId,
  needBoth = false,
}: {
  memberId: string;
  needBoth?: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: saved } = useOwnContact(memberId);
  const [draft, setDraft] = useState<ContactDraft | null>(null);
  const value = draft ?? draftOf(saved);

  const save = useMutation({
    mutationFn: () => saveContactDetails(value.flatNo, value.phone),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["own-contact", memberId] });
      void queryClient.invalidateQueries({ queryKey: ["all-contacts"] });
      setDraft(null);
    },
  });

  const changed =
    cleanFlatNo(value.flatNo) !== (saved?.flatNo ?? null) ||
    cleanPhone(value.phone) !== (saved?.phone ?? null);
  const ok =
    contactProblem(value.flatNo, value.phone) === null &&
    (!needBoth || (cleanFlatNo(value.flatNo) !== null && cleanPhone(value.phone) !== null));

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit}>
      <ContactFields value={value} onChange={setDraft} needBoth={needBoth} />
      <Button tone="primary" type="submit" disabled={!changed || !ok || save.isPending}>
        {save.isPending ? "Saving…" : "Save contact details"}
      </Button>
      {save.error ? <Notice tone="attention">{save.error.message}</Notice> : null}
    </form>
  );
}
